import {Component, EventEmitter, forwardRef, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {v4 as uuid} from 'uuid';
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {FormulaComponent, NewUserVariableEvent} from '@shared/components/formula-builder/formula/formula.component';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {AzSwitchItem} from '@azigrene/components/lib/form/switch-button/models/switch-item.model';
import {FormulaDinamicaTestRequest} from "@shared/models/formula-dinamica";

export class FormulaVariableDefinition {
  code!: string;
  info!: string;
  group!: string;
}

export type FormulaOperatorType = 'variable' | 'operator' | 'blank' | 'variable_sis' | 'number';
export type FormulaVariableType = 'ESCALAR' | 'VECTORIAL' | 'FORMULA';

// FormulaOperator class
export class FormulaOperator {
  id: string;
  name: FormulaOperatorType;

  constructor(name: FormulaOperatorType) {
    this.name = name;
    this.id = uuid();
  }
}

// FormulaVariable class
export class FormulaVariable extends FormulaOperator {
  var: string;
  tipo: FormulaVariableType;
  value?: number | number[] | FormulaOperator[];

  constructor(type: FormulaOperatorType, variable: string, tipo: FormulaVariableType) {
    super(type);
    this.var = variable;
    this.tipo = tipo;
  }
}

// FormulaOp class
export class FormulaOp extends FormulaOperator {
  text: string;

  constructor(text: string) {
    super('operator');
    this.text = text;
  }
}

// FormulaNumber class
export class FormulaNumber extends FormulaOperator {
  text: number;

  constructor(text: string) {
    super('number');
    this.text = Number(text);
  }
}

@Component({
  selector: 'az-formula-builder',
  templateUrl: './formula-builder.component.html',
  styleUrls: ['./formula-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormulaBuilderComponent),
      multi: true,
    },
  ],
})
export class FormulaBuilderComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() disabled: boolean;
  @Input() showTest = true;
  @Input() groupSysVars = false;
  @HostBinding('class') class = 'az-formula-builder';
  @Input() systemVariables: FormulaVariableDefinition[] = [];
  @Input() customVariables: FormulaVariable[] = [];
  @Input() translations: any = {};
  @Input() vectorialVariablesLength = 6;
  @Input() validateFunction: (formula: FormulaOperator[]) => Observable<boolean>;
  @Input() testFunction: (request: FormulaDinamicaTestRequest, cuentaId: number) => Observable<any>;
  @Input() downloadFunction: (request: FormulaDinamicaTestRequest, cuentaId: number) => Observable<any>;
  @Input() showCuentaInput: boolean = true;
  @Input() excludeFormulaVariableTypes: FormulaVariableType[] = [];

  @Output() formulaChanged = new EventEmitter();
  @Output() variableCreated = new EventEmitter<FormulaVariable>();
  @Output() variableUpdated = new EventEmitter<FormulaVariable>();
  @Output() variableDeleted = new EventEmitter<FormulaVariable>();
  formula: FormulaOperator[] = [];

  @ViewChild('formulaEditor') formulaEditor: FormulaComponent;

  visibleFormulaVariableType: FormulaVariableType[] = ["ESCALAR", "VECTORIAL"];
  initialFormula: FormulaOperator[] = [];
  _systemVariables: FormulaVariableDefinition[] = [];
  _systemVariablesByGroup: Map<string, FormulaVariableDefinition[]> = new Map<string, FormulaVariableDefinition[]>();
  _userVariables: FormulaVariable[] = [];
  _newVariableDropdownVisible: boolean;
  _newVariableForm: FormGroup;
  _activeTab: 'edit' | 'test' = 'edit';
  _switchItems: AzSwitchItem[] = [];
  operators: FormulaOp[] = [new FormulaOp('('), new FormulaOp(')'), new FormulaOp('+'), new FormulaOp('-'), new FormulaOp('/'), new FormulaOp('*')];
  hasFormulaChanged: boolean;
  protected _newVariableEvent: NewUserVariableEvent;
  onChange: any = () => {};
  onTouch: any = () => {};
  _value: any;
  formControlDisabled = false;
  validFormula: boolean = false;
  invalidFormula: boolean = undefined;

  writeValue(val) {
    this._value = val;
    this.formula = val || [];
    this.initialFormula = JSON.parse(JSON.stringify(this._value));
    this.validFormula = this.formula?.length > 0;
    if(this.validFormula) {
      this.updateUserVariables();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDisabled = isDisabled;
  }


  updateVariableSuccessFunction = (name: string, previousName: string, tipo: FormulaVariableType) => {
    this.formulaEditor.updateVariable(previousName,name);
  }

  constructor(private fb: FormBuilder) {
    this._newVariableForm = this.fb.group({
      name: ['', Validators.required],
      tipo: ['ESCALAR', Validators.required]
    })
  }

  ngOnInit(): void {
    this.initialFormula = JSON.parse(JSON.stringify(this.formula));
    this.updateSystemVariables();
    this.updateUserVariables();

    this._switchItems = [
      {label: 'misc.editar',value: 'edit'},
      {label: 'misc.test', value: 'test'},
    ];

    this.visibleFormulaVariableType = this.visibleFormulaVariableType.filter(type => !this.excludeFormulaVariableTypes.includes(type));
  }

  ngOnChanges(changes:SimpleChanges): void {
    if(changes.systemVariables){
      this.updateSystemVariables();
    }
  }

  updateSystemVariables(){
    this._systemVariables = [...this.systemVariables];
    this._systemVariables = this._systemVariables.sort((a,b) => a.code.localeCompare(b.code));
    this._systemVariablesByGroup = this._systemVariables.reduce((map, formula) => {
      if (!map.has(formula.group)) {
        map.set(formula.group, []);
      }

      map.get(formula.group)?.push(formula);

      return map;
    }, new Map<string, FormulaVariableDefinition[]>());
  }

  updateUserVariables(){
    if(!this.formula) return;

    const customVariables: FormulaVariable[] = this.formula.filter(fi => fi.name == "variable").filter((item: FormulaVariable, index, self) =>
      index === self.findIndex((t: FormulaVariable) => t.var === item.var)
    ).map(fi => fi as FormulaVariable);
    this._userVariables = [...customVariables];
    this._userVariables.sort((a, b) => a.var.localeCompare(b.var));
  }

  addVariableDefinition(name: string, type: FormulaVariableType = 'ESCALAR'): void {
    if(this._userVariables.map(uv => uv.var).includes(name)) {
      this._newVariableForm.controls.name.setErrors({'duplicated-name': true});
      return;
    }

    const variable = new FormulaVariable('variable', name, type);
    this._userVariables.push(variable);
  }

  private removeVariable(id: any) {
    const variable = this._userVariables.find((v) => v.id === id);
    this._userVariables.splice(this._userVariables.indexOf(variable), 1);
  }


  updateFormula(formula: FormulaOperator[]) {
    this.formula = formula;

    if(JSON.stringify(formula) !== JSON.stringify(this.initialFormula)) {
      this.hasFormulaChanged = true;
      this.validFormula = false;
    }
  }

  saveFormula() {
    this.formulaChanged.emit(this.formula);
    this.hasFormulaChanged = false;
    this.initialFormula = JSON.parse(JSON.stringify(this.formula));

    if (!this.disabled) {
      this._value = JSON.parse(JSON.stringify(this.formula));
      this.onChange(JSON.parse(JSON.stringify(this.formula)));
      this.onTouch();
      if(this.validateFunction) {
        this.validateFunction(this._value).subscribe(ok => {
          this.validFormula = ok;
        });
      }
    }
  }

  revertFormula() {
    this.formula = JSON.parse(JSON.stringify(this.initialFormula));
    this.hasFormulaChanged = false;
    this.validFormula = true;
    this.formulaChanged.emit(this.formula);
  }

  updateUserVariable(variable: string) {
    this.removeVariable(variable);
    this.addVariableDefinition(this._newVariableForm.value?.name, this._newVariableForm.value?.tipo);
    this.variableUpdated.emit(new FormulaVariable('variable', this._newVariableForm.value?.name, this._newVariableForm.value?.tipo));
    if(this._newVariableEvent.success)
      this._newVariableEvent.success(this._newVariableForm.value?.name, variable, this._newVariableForm.value?.tipo);
    this.updateUserVariables();
    this.closeNewVariableDropdown();
  }

  registerUserVariable() {
    this.addVariableDefinition(this._newVariableForm.value?.name, this._newVariableForm.value?.tipo);
    this.variableCreated.emit(new FormulaVariable('variable', this._newVariableForm.value?.name, this._newVariableForm.value?.tipo));
    if(this._newVariableEvent.success)
      this._newVariableEvent.success(this._newVariableForm.value?.name,this._newVariableForm.value?.name, this._newVariableForm.value?.tipo);
    this.updateUserVariables();
    this.closeNewVariableDropdown();
  }

  unregisterUserVariable(variable: string) {
    this.removeVariable(variable);
    this.updateUserVariables();
    this.variableDeleted.emit(new FormulaVariable('variable', this._newVariableForm.value?.name, this._newVariableForm.value?.tipo));
  }

  closeNewVariableDropdown() {
    this._newVariableDropdownVisible = false;
    this._newVariableForm.reset();
  }

  openNewVariableDropdown(variableEvent: NewUserVariableEvent) {
    this._newVariableDropdownVisible = true;
    this._newVariableEvent = variableEvent;
    this._newVariableForm.setValue({name: variableEvent.name, tipo: 'ESCALAR'})
  }

  validateFormulaAndSave() {
    this.validateFunction(this.formula).pipe(take(1)).subscribe(ok => {
      this.validFormula = ok;
      this.invalidFormula = !ok;
      if(ok) this.saveFormula();
    })
  }
}
